import z from 'zod'
import { ManagerTaskIdSchema } from '../task'
import { TeamIdSchema } from '../ids'
import { TinyTeamSchema, TinyUserSchema } from '../entity'
import { ProgramUserDataSchema } from './programUser'
import { ProgramTaskIdSchema } from './programTask'

export const ProgramUserGroupingSchema = z.object({
  type: z.literal('user'),
})

export const ProgramTeamGroupingSchema = z.object({
  type: z.literal('team'),
  level: z.number(),
  include: TeamIdSchema.array().optional(),
})

export const ProgramTaskGroupingSchema = z.object({
  type: z.literal('task'),
})

export const ProgramStatsGroupingSchema = z.discriminatedUnion('type', [
  ProgramUserGroupingSchema,
  ProgramTeamGroupingSchema,
  ProgramTaskGroupingSchema,
])

export type ProgramStatsGrouping = z.infer<typeof ProgramStatsGroupingSchema>

export const ProgramStatsDataSchema = z.object({
  completedTasks: z.number(),
  totalTasks: z.number(),
  completionRate: z.number().min(0).max(100),
  overdueTasks: z.number().optional(),
  lateSubmissionTasks: z.number().optional(),
  totalNeeded: z.number().optional(),
  totalProgress: z.number().optional(),
  medianStartDate: z.number().optional(),
})

export type ProgramStatsData = z.infer<typeof ProgramStatsDataSchema>

const ProgramUserStatsEntitySchema = z.object({
  ...TinyUserSchema.shape,
  ...ProgramUserDataSchema.partial().pick({ startDate: true, completionDate: true, status: true }).shape,
})
export type ProgramUserStatsEntity = z.infer<typeof ProgramUserStatsEntitySchema>

export const ProgramUserStatsOutputSchema = {
  type: z.literal('user'),
  entity: ProgramUserStatsEntitySchema,
  stats: ProgramStatsDataSchema,
}

export const ProgramTeamStatsOutputSchema = {
  type: z.literal('team'),
  entity: TinyTeamSchema,
  stats: ProgramStatsDataSchema,
}

export const TinyProgramTaskSchema = z.object({
  taskId: ProgramTaskIdSchema,
  name: z.string(),
  body: z.string().optional(),
})
export type TinyProgramTask = z.infer<typeof TinyProgramTaskSchema>

export const TinyManagerTaskSchema = z.object({
  taskId: ManagerTaskIdSchema,
  name: z.string(),
  body: z.string().optional(),
})
export type TinyManagerTask = z.infer<typeof TinyManagerTaskSchema>

export const ProgramTaskStatsOutputSchema = {
  type: z.literal('task'),
  entity: z.union([TinyProgramTaskSchema, TinyManagerTaskSchema]),
  stats: ProgramStatsDataSchema,
}

const ProgramStatsEntitySchema = z.union([TinyUserSchema, TinyTeamSchema, TinyProgramTaskSchema, TinyManagerTaskSchema])
export type ProgramStatsEntity = z.infer<typeof ProgramStatsEntitySchema>

export const ProgramStatsResultsSchema = z.discriminatedUnion('type', [
  z.object(ProgramUserStatsOutputSchema),
  z.object(ProgramTeamStatsOutputSchema),
  z.object(ProgramTaskStatsOutputSchema),
])
export type ProgramStatsResults = z.infer<typeof ProgramStatsResultsSchema>
export type TypedProgramStatsResults<T extends ProgramStatsResults['type']> = Extract<ProgramStatsResults, { type: T }>
